import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, CircleAlert, LoaderCircle, Zap } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/_shared/components/ui/badge';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { ActionsBar } from '../components/ActionsBar';
import { ActionSection } from '../components/ActionSection';
import { DryRunResults } from '../components/DryRunResults';
import { QuerySection } from '../components/QuerySection';
import { RunHistory } from '../components/RunHistory';
import type { RenderedPreview, WorkflowRun } from '../types';

function statusVariant(status: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'active') return 'default';
  if (status === 'paused') return 'outline';
  return 'secondary';
}

interface DryRunData {
  sample: Record<string, unknown>[];
  renderedPreview?: RenderedPreview;
}

export function WorkflowDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const [confirmAction, setConfirmAction] = useState<'cursor' | 'deliveries' | 'delete' | null>(null);
  const [dryRunPreview, setDryRunPreview] = useState<DryRunData | null>(null);

  const { data, isLoading, error } = useQuery(orpc.workflow.get.queryOptions({ input: { id: id! } }));
  const workflow = data?.workflow;
  usePageTitle(workflow?.name);

  const { data: runsData, isLoading: loadingRuns } = useQuery({
    ...orpc.workflow.listRuns.queryOptions({ input: { workflowId: id! } }),
    enabled: !!id,
  });
  const runs = (runsData?.runs ?? []) as WorkflowRun[];

  const invalidateAll = () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.workflow.listRuns.queryOptions({
        input: { workflowId: id! },
      }).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.workflow.get.queryOptions({ input: { id: id! } }).queryKey,
    });
  };

  const runMutation = useMutation({
    mutationFn: () => orpc.workflow.run.call({ id: id! }),
    onSuccess: invalidateAll,
  });

  const dryRunMutation = useMutation({
    mutationFn: () => orpc.workflow.run.call({ id: id!, dryRun: true }),
    onSuccess: (data) => {
      invalidateAll();
      const run = data.run as { sample: Record<string, unknown>[]; renderedPreview?: RenderedPreview };
      setDryRunPreview({
        sample: run.sample ?? [],
        renderedPreview: run.renderedPreview,
      });
    },
  });

  const resetCursorMutation = useMutation({
    mutationFn: () => orpc.workflow.resetCursor.call({ id: id! }),
    onSuccess: () => {
      setConfirmAction(null);
      invalidateAll();
    },
  });

  const resetDeliveriesMutation = useMutation({
    mutationFn: () => orpc.workflow.resetDeliveries.call({ id: id! }),
    onSuccess: () => {
      setConfirmAction(null);
      invalidateAll();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => orpc.workflow.delete.call({ id: id! }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.workflow.list.queryOptions({ input: {} }).queryKey,
      });
      void navigate(wp('/workflows'));
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error || !workflow) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <CircleAlert size={32} className="mb-4 text-red-500" />
        <h2 className="text-base font-medium">Workflow not found</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error?.message ?? 'The workflow could not be loaded.'}</p>
        <Link to={wp('/workflows')} className="mt-4 text-sm text-primary hover:underline">
          &larr; Back to workflows
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
          <Link to={wp('/workflows')} className="hover:text-foreground">
            <span className="mr-1">&larr;</span>Workflows
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <Zap size={20} className="text-muted-foreground" />
          <h1 className="text-lg font-semibold text-foreground">{workflow.name}</h1>
          <Badge variant={statusVariant(workflow.status)} className="text-[0.625rem]">
            {workflow.status}
          </Badge>
          <Badge variant="outline" className="text-[0.625rem]">
            {workflow.actionMode === 'per_row' ? 'Customer-facing' : 'Internal'}
          </Badge>
        </div>
        {workflow.description && <p className="mt-1 text-sm text-muted-foreground">{workflow.description}</p>}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 px-6 py-6">
          <QuerySection workflow={workflow} />

          {/* Visual Connector */}
          <div className="flex justify-center">
            <div className="flex flex-col items-center">
              <div className="h-4 w-px bg-border" />
              <ArrowDown size={16} className="text-muted-foreground" />
            </div>
          </div>

          <ActionSection workflow={workflow} />

          <ActionsBar
            confirmAction={confirmAction}
            setConfirmAction={setConfirmAction}
            onRun={() => runMutation.mutate()}
            onDryRun={() => dryRunMutation.mutate()}
            onResetCursor={() => resetCursorMutation.mutate()}
            onResetDeliveries={() => resetDeliveriesMutation.mutate()}
            onDelete={() => deleteMutation.mutate()}
            runPending={runMutation.isPending}
            dryRunPending={dryRunMutation.isPending}
            resetCursorPending={resetCursorMutation.isPending}
            resetDeliveriesPending={resetDeliveriesMutation.isPending}
            deletePending={deleteMutation.isPending}
            runError={runMutation.error}
            dryRunError={dryRunMutation.error}
          />

          {dryRunPreview && <DryRunResults data={dryRunPreview} actionType={workflow.actionType} />}

          <RunHistory runs={runs} isLoading={loadingRuns} />
        </div>
      </div>
    </div>
  );
}
